import React from "react";
import { ResponsiveLine } from "@nivo/line";
import { dateToString, dateToMonthYear, dateToShortString } from "dateUtils";

// ─── Helpers ──────────────────────────────────────────────────────────────

function isMonthlyData(dateStr) {
  return dateStr.split("-").length === 2;
}

// Date strings from the backend are Chicago calendar days (e.g. "2026-05-15").
// Anchor at UTC noon so re-formatting with `timeZone: "America/Chicago"` (Chicago
// is UTC-5/-6) stays inside the same calendar day instead of slipping back one.
function strToDate(dateStr) {
  const [year, month] = dateStr.split("-");
  return new Date(Date.UTC(year, month - 1, 15, 12));
}

function fullStrToDate(dateStr) {
  const [year, month, day] = dateStr.split("-");
  return new Date(Date.UTC(year, month - 1, day, 12));
}

function formatTooltipDate(dateStr) {
  if (isMonthlyData(dateStr)) return dateToMonthYear(strToDate(dateStr));
  return dateToString(fullStrToDate(dateStr));
}

function formatId(id) {
  if (id === "arrUp") return "Reservations";
  if (id === "cancel") return "Cancelled";
  if (id === "noshow") return "No Show";
  return id;
}

// ─── Tooltip ──────────────────────────────────────────────────────────────

function PointTooltip({ point }) {
  return (
    <div
      style={{
        background: "#111115",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: "8px",
        padding: "9px 13px",
        fontSize: "13px",
        color: "#f0ede8",
        pointerEvents: "none",
        boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
      }}
    >
      <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", opacity: 0.5, marginBottom: "6px" }}>
        {formatTooltipDate(point.data.x)}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
        <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: point.color, flexShrink: 0 }} />
        <span style={{ opacity: 0.8 }}>{formatId(point.serieId)}</span>
        <span style={{ fontWeight: 700, marginLeft: "auto", paddingLeft: 10 }}>{point.data.y}</span>
      </div>
    </div>
  );
}

// ─── Chart ────────────────────────────────────────────────────────────────

function LineChart({ data, view }) {
  const dailyProps = {
    axisBottom: {
      tickSize: 5,
      tickPadding: 10,
      tickRotation: 15,
      tickValues: (() => {
        if (!data[0]?.data?.length) return [];
        if (view === "week") return data[0].data.map((d) => d.x);
        if (view === "month") return data[0].data.filter((_, i) => i % 2 === 0).map((d) => d.x);
        return data[0].data.map((d) => d.x);
      })(),
      format: (value) => dateToShortString(fullStrToDate(value)),
    },
  };

  const monthlyProps = {
    axisBottom: {
      tickSize: 5,
      tickPadding: 10,
      tickRotation: 15,
      tickValues: (() => {
        if (!data[0]?.data?.length) return [];
        if (view === "year") return data[0].data.filter((_, i) => i % 2 === 0).map((d) => d.x);
        if (view === "allTime") {
          const interval = Math.max(1, Math.floor(data[0].data.length / 8));
          return data[0].data.filter((_, i) => i % interval === 0).map((d) => d.x);
        }
        return data[0].data.map((d) => d.x);
      })(),
      format: (value) => dateToMonthYear(strToDate(value)),
    },
  };

  const propsToUse = (() => {
    if (!data[0]?.data?.length) return dailyProps;
    return isMonthlyData(data[0].data[0].x) ? monthlyProps : dailyProps;
  })();

  return (
    <ResponsiveLine
      data={data}
      margin={{ top: 30, right: 20, bottom: 40, left: 30 }}
      axisTop={null}
      colors={["#d3963add", "#a09c9c", "#c64949"]}
      lineWidth={2}
      curve="monotoneX"
      pointSize={7}
      pointBorderWidth={2}
      useMesh={true}
      crosshairType="x"
      tooltip={PointTooltip}
      {...propsToUse}
      theme={{
        axis: {
          ticks: {
            line: { stroke: "#f8f4f1c1" },
            text: { fill: "#f8f4f1c1" },
          },
          domain: {
            line: { stroke: "#f8f4f1c1" },
          },
        },
        grid: {
          line: { stroke: "#f8f4f1c1", strokeWidth: 1, opacity: 0.15 },
        },
      }}
    />
  );
}

export default LineChart;
